import { useEffect, useState } from "react";
import { Avatar, Box, Button, Chip, IconButton, Skeleton, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";

import { colors } from "../../../theme/palette";
import {
  ArrowBackIcon,
  EventDetailsDescriptionIcon,
  EventDetailsDurationIcon,
  EventDetailsLocationIcon,
  EventPrivacyIcon,
  EventTypeIcon,
} from "../../../assets/icons/MUI/EventsIcons";
import { cancelEvent, getEventDetails } from "../../../api/events";
import { useEventDataFormatter } from "../../../hooks/useEventDataFormatter";
import { useProfileData } from "../../../hooks/useProfileData";
import { getEntityDetails } from "../../../api/entity";
import { UserInfo } from "../../../types";
import { useDataFormatter } from "../../../hooks/useDataFormatter";

import ConfirmCancelEventPopUp from "../EventsList/ConfirmCancelEventPopUp";
import Guests from "./Guests";
import APP_ROUTES from "../../../routers/routeConstants";

const EventDetails = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const [isCanceled, setIsCanceled] = useState(false);
  const [isOwnEvent, setIsOwnEvent] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState<{
    id: number;
    title: string;
  } | null>(null);
  const [cancelEventComment, setCancelEventComment] = useState<string | null>(null);

  const {
    data: eventDetails,
    isLoading: isEventDetailsLoading,
    refetch,
  } = useQuery({
    queryKey: ["eventDetails", eventId],
    queryFn: () => getEventDetails(eventId),
    enabled: !!eventId,
    staleTime: 0,
  });

  const {
    id,
    name,
    description,
    eventType,
    eventStatus,
    eventPrivacy,
    status,
    eventLocation,
    eventLat,
    eventLon,
    authorId,
    startingHour,
    endHour,
    totalPlayersInvitedNum,
    acceptedPlayersNum,
  } = useEventDataFormatter(eventDetails?.event);

  const { isLoaded: isGoogleMapLoaded } = useJsApiLoader({
    // googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string,
    libraries: ["places"],
  });

  useEffect(() => {
    setIsCanceled(status === "CANCELLED");
  }, [status]);

  const handleCancelEvent = async () => {
    try {
      const body = {
        cancelEventReason: cancelReason?.title,
        cancelEventComment,
      };
      await cancelEvent(id, body);
      setIsCanceled(true);
      setOpenDialog(false);
    } catch {}
  };

  const { data: userData, isLoading: isUserDataLoading } = useProfileData();
  const userId = userData?.id;

  useEffect(() => {
    setIsOwnEvent(userId === authorId);
  }, [userId, authorId]);

  const {
    data: authorData,
    isLoading: isAuthorDataLoading,
    isError: isAuthorDataHasError,
  } = useQuery<UserInfo>({
    queryKey: ["entityProfileInfo", authorId],
    queryFn: () => getEntityDetails(`${authorId}`),
    enabled: !!authorId,
  });

  const {
    fullName: organizerName,
    firstTwoChr: organizerFirstTwoChr,
    role: organizerRole,
    media: organizerMedia,
    countryName: organizerCountry,
  } = useDataFormatter(authorData);

  return (
    <Box
      sx={{
        padding: "12px",
        background: colors.White,
        borderRadius: "8px",
        border: "1px solid #EAECF0",
        width: "100%",
      }}
    >
      <Typography
        variant="h4ExtraBold"
        sx={{
          color: colors.Black100,
          display: "flex",
          columnGap: "8px",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <IconButton
          onClick={() => navigate(APP_ROUTES.EVENTS)}
          sx={{
            transform: currentLang === "ar" ? "rotate(180deg)" : "unset",
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        {t("pages.events.eventDetails.eventDetails")}
      </Typography>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "16px",
        }}
      >
        <Box sx={{ display: "flex", columnGap: "12px" }}>
          <Chip
            label={eventType}
            sx={{
              padding: "2px 4px",
              minWidth: "64px",
              height: "fit-content",
              color: colors.Dark1000,
              background: colors.SecondaryMint,
              "& .MuiChip-label": {
                padding: 0,
              },
            }}
          />
          <Chip
            label={isCanceled ? t(`pages.events.eventList.CANCELLED`) : eventStatus}
            sx={{
              padding: "2px 4px",
              minWidth: "64px",
              height: "fit-content",
              color: colors.White,
              background: isCanceled
                ? colors.Dark600
                : status === "FINISHED"
                ? colors.Dark600
                : status === "SOON"
                ? colors.Yellow200
                : status === "ACTIVE"
                ? colors.Red100
                : colors.Dark600,
              "& .MuiChip-label": {
                padding: 0,
              },
            }}
          />
        </Box>

        {!isCanceled && status !== "FINISHED" && isOwnEvent && (
          <Button
            variant="outlined-sm"
            sx={{
              borderColor: colors.Red200,
              color: colors.Red200,
              "&:hover": {
                backgroundColor: colors.Red25,
                color: colors.Red100,
                borderColor: colors.Red100,
              },
              fontFamily: "Montserrat",
              fontSize: "14px",
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: "20px",
            }}
            onClick={() => setOpenDialog(true)}
          >
            {t("pages.events.eventList.cancelMatch")}
          </Button>
        )}
      </Box>

      <Typography variant="body16ExtraBold" sx={{ color: colors.Dark1000, display: "block", marginBottom: "16px" }}>
        {name}
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          columnGap: "25px",
          rowGap: "40px",
        }}
      >
        <Box sx={{ width: "100%" }}>
          {!isGoogleMapLoaded ? (
            <Skeleton variant="text" width="416px" height="200px" animation="wave" />
          ) : eventLat && eventLon ? (
            <GoogleMap
              mapContainerStyle={{
                width: "100%",
                height: "200px",
                marginBottom: "24px",
              }}
              center={{ lat: eventLat, lng: eventLon }}
              zoom={15}
            >
              <Marker position={{ lat: eventLat, lng: eventLon }} />
            </GoogleMap>
          ) : (
            <Typography variant="body12SemiBold" sx={{ marginBottom: "24px" }}>
              No location available
            </Typography>
          )}
          <Typography
            variant="body16ExtraBold"
            sx={{
              color: colors.Dark1000,
              display: "block",
              marginBottom: "16px",
            }}
          >
            {t("pages.events.eventDetails.organizer")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              columnGap: "12px",
              alignItems: "center",
              paddingBottom: "16px",
              borderBottom: "1px solid #E7E7E7",
              marginBottom: "16px",
            }}
          >
            <Avatar src={isAuthorDataLoading ? undefined : organizerMedia} sx={{ width: "48px", height: "48px" }}>
              {isAuthorDataLoading ? <Skeleton variant="circular" animation="wave" /> : organizerFirstTwoChr}
            </Avatar>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="body14SemiBold" sx={{ color: colors.Dark1000 }}>
                {isAuthorDataLoading ? <Skeleton variant="text" width="55px" animation="wave" /> : organizerName}
              </Typography>
              <Typography variant="body13Regular" sx={{ color: colors.Dark1000 }}>
                {isAuthorDataLoading ? (
                  <Box sx={{ display: "flex", columnGap: "20px" }}>
                    <Skeleton variant="text" width="28px" animation="wave" />
                    <Skeleton variant="text" width="36px" animation="wave" />
                  </Box>
                ) : (
                  `${organizerRole} · ${organizerCountry}`
                )}
              </Typography>
            </Box>
          </Box>
          <Typography
            variant="body16ExtraBold"
            sx={{
              color: colors.Dark1000,
              display: "block",
              marginBottom: "25px",
            }}
          >
            {t("pages.events.eventDetails.details")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              rowGap: "16px",
              paddingBottom: "16px",
              borderBottom: "1px solid #E7E7E7",
              marginBottom: "16px",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", rowGap: "4px" }}>
              <Typography
                variant="body16ExtraBold"
                sx={{
                  color: colors.Dark1000,
                  display: "flex",
                  columnGap: "4px",
                  alignItems: "center",
                }}
              >
                <EventDetailsDescriptionIcon />
                {t("pages.events.eventDetails.description")}
              </Typography>
              <Typography variant="body14Regular" sx={{ color: colors.Dark800 }}>
                {isEventDetailsLoading ? (
                  <>
                    <Skeleton variant="text" width="416px" animation="wave" />
                    <Skeleton variant="text" width="416px" animation="wave" />
                    <Skeleton variant="text" width="416px" animation="wave" />
                    <Skeleton variant="text" width="150px" animation="wave" />
                  </>
                ) : (
                  description
                )}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", rowGap: "4px" }}>
              <Typography
                variant="body16ExtraBold"
                sx={{
                  color: colors.Dark1000,
                  display: "flex",
                  columnGap: "4px",
                  alignItems: "center",
                }}
              >
                <EventDetailsLocationIcon />
                {t("pages.events.eventDetails.location")}
              </Typography>
              <Typography variant="body14Regular" sx={{ color: colors.Dark800 }}>
                {isEventDetailsLoading ? <Skeleton variant="text" width="416px" animation="wave" /> : eventLocation}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "row", columnGap: "4px", alignItems: "center" }}>
              <Typography
                variant="body16ExtraBold"
                sx={{
                  color: colors.Dark1000,
                  display: "flex",
                  columnGap: "4px",
                  alignItems: "center",
                }}
              >
                <EventDetailsDurationIcon />
                {t("pages.events.eventDetails.duration")}
              </Typography>
              <Typography variant="body14Regular" sx={{ color: colors.Dark800 }}>
                {isEventDetailsLoading ? (
                  <Skeleton variant="text" width="125px" animation="wave" />
                ) : (
                  `${startingHour} - ${endHour}`
                )}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "row", columnGap: "4px" }}>
              <Typography
                variant="body16ExtraBold"
                sx={{
                  color: colors.Dark1000,
                  display: "flex",
                  columnGap: "4px",
                  alignItems: "center",
                }}
              >
                <EventTypeIcon />
                {t("pages.events.eventDetails.type")}
              </Typography>
              <Typography variant="body14Regular" sx={{ color: colors.Dark800 }}>
                {isEventDetailsLoading ? <Skeleton variant="text" width="53px" animation="wave" /> : eventType}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "row", columnGap: "4px" }}>
              <Typography
                variant="body16ExtraBold"
                sx={{
                  color: colors.Dark1000,
                  display: "flex",
                  columnGap: "4px",
                  alignItems: "center",
                }}
              >
                <EventPrivacyIcon />
                {t("pages.events.eventDetails.privacy")}
              </Typography>
              <Typography variant="body14Regular" sx={{ color: colors.Dark800 }}>
                {isEventDetailsLoading ? <Skeleton variant="text" width="47px" animation="wave" /> : eventPrivacy}
              </Typography>
            </Box>
          </Box>
          <Typography variant="body16ExtraBold" sx={{ display: "block", marginBottom: "16px" }}>
            {t("pages.events.eventDetails.rules")}
          </Typography>
          <Typography variant="body16ExtraBold" sx={{ display: "block", marginBottom: "25px" }}>
            {t("pages.events.eventDetails.descriptionAndPlayingRules")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              rowGap: "15px",
            }}
          >
            <Typography
              variant="body14Regular"
              sx={{
                color: colors.Dark600,
              }}
            >
              <Typography
                variant="body16ExtraBold"
                sx={{
                  color: colors.Green100,
                }}
              >
                {t("pages.events.createEvent.freeGames")}{" "}
              </Typography>
              {t("pages.events.createEvent.freeGamesDescription")}
            </Typography>
            <Typography
              variant="body14Regular"
              sx={{
                color: colors.Dark600,
              }}
            >
              <Typography
                variant="body16ExtraBold"
                sx={{
                  color: colors.Green100,
                }}
              >
                {t("pages.events.createEvent.paidGames")}{" "}
              </Typography>
              {t("pages.events.createEvent.paidGamesDescription")}
            </Typography>
            <Typography
              variant="body14Regular"
              sx={{
                color: colors.Dark600,
              }}
            >
              <Typography
                variant="body16ExtraBold"
                sx={{
                  color: colors.Green100,
                }}
              >
                {t("pages.events.createEvent.gameScheduling")}{" "}
              </Typography>
              {t("pages.events.createEvent.gameSchedulingDescription")}
            </Typography>
            <Typography
              variant="body14Regular"
              sx={{
                color: colors.Dark600,
              }}
            >
              <Typography
                variant="body16ExtraBold"
                sx={{
                  color: colors.Green100,
                }}
              >
                {t("pages.events.createEvent.gameCancellation")}{" "}
              </Typography>
              {t("pages.events.createEvent.gameCancellationDescription")}
            </Typography>
            <Typography
              variant="body14Regular"
              sx={{
                color: colors.Dark600,
              }}
            >
              <Typography
                variant="body16ExtraBold"
                sx={{
                  color: colors.Green100,
                }}
              >
                {t("pages.events.createEvent.lateArrival")}{" "}
              </Typography>
              {t("pages.events.createEvent.lateArrivalDescription")}
            </Typography>
            <Typography
              variant="body14Regular"
              sx={{
                color: colors.Dark600,
              }}
            >
              <Typography
                variant="body16ExtraBold"
                sx={{
                  color: colors.Green100,
                }}
              >
                {t("pages.events.createEvent.paymentProcessing")}{" "}
              </Typography>
              {t("pages.events.createEvent.paymentProcessingDescription")}
            </Typography>
            <Typography
              variant="body14Regular"
              sx={{
                color: colors.Dark600,
              }}
            >
              <Typography
                variant="body16ExtraBold"
                sx={{
                  color: colors.Green100,
                }}
              >
                {t("pages.events.createEvent.codeOfConduct")}
              </Typography>
              {t("pages.events.createEvent.codeOfConductDescription")}
            </Typography>
          </Box>
        </Box>
        <Guests
          eventId={eventId}
          totalPlayersInvitedNum={totalPlayersInvitedNum}
          acceptedPlayersNum={acceptedPlayersNum}
          isOwnEvent={isOwnEvent}
          isCanceled={isCanceled}
          status={status}
          isEventDetailsLoading={isEventDetailsLoading}
          refetchEvent={refetch}
        />
      </Box>
      <ConfirmCancelEventPopUp
        openDialog={openDialog}
        handleCancelEvent={handleCancelEvent}
        handleCloseDialog={() => setOpenDialog(false)}
        cancelReason={cancelReason}
        setCancelReason={setCancelReason}
        cancelEventComment={cancelEventComment}
        setCancelEventComment={setCancelEventComment}
      />
    </Box>
  );
};

export default EventDetails;
